import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useRef } from 'react'
import { z } from 'zod'

import { useAuth } from '../../auth-provider/auth-context'
import { API_URL, DayOfWeek, VehicleEngine } from '../../constants/constants'
import { showNotificationWithError, showNotificationWithSuccess } from '../helpers'
import { vehicleClassSchema } from '../vehicle-class/api'

export const metroAreaSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  timezone_designation: z.string().nullable(),
  moment_timezone: z.string().nullable(),
  timezone: z.string().nullable(),
})

export type MetroArea = z.infer<typeof metroAreaSchema>

const branchVehicleClassesResponseSchema = z.array(vehicleClassSchema)

export type WorkingHoursDay = {
  open: string | null
  close: string | null
  is_working_day: boolean
}
export type WorkingHoursWeek = {
  [key in DayOfWeek]: WorkingHoursDay
}

export type UpdateBranchData = {
  name?: string
  address?: string
  phone_number?: string
  disable_notifications_outside_working_hours?: boolean
  labor_rate?: number
  vehicle_class_ids?: string[]
  vehicle_engines?: VehicleEngine[]
  working_hours?: WorkingHoursWeek
}

export const branchImageSchema = z.object({
  id: z.string().uuid(),
  provider_branch_id: z.string().uuid(),
  url: z.string(),
  is_cover: z.boolean(),
  order: z.number(),
})

export type BranchImage = z.infer<typeof branchImageSchema>

export const branchImagesResponseSchema = z.object({
  cover_image: branchImageSchema.nullable(),
  images: z.array(branchImageSchema),
})

export type BranchImagesResponse = z.infer<typeof branchImagesResponseSchema>

export const providerBranchSchema = z.object({
  id: z.string().uuid(),
  provider_id: z.string().uuid().nullable(),
  stripe_account_id: z.string(),
  stripe_payouts_enabled: z.boolean(),
  name: z.string(),
  bio: z.string(),
  longitude: z.string().nullable(),
  latitude: z.string().nullable(),
  working_hours: z.any().nullable(), // todo: make this a proper schema
  address: z.string(),
  phone_number: z.string(),
  rating_decimal: z.string().nullable(),
  reviews_count: z.number(),
  text_notification: z.boolean(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime().nullable(),
  google_reviews_count: z.number().nullable(),
  zip_code: z.string().nullable(),
  metro_area_id: z.string().uuid().nullable(),
  disable_notifications_outside_working_hours: z.boolean(),
  vehicle_engines: z.array(z.enum(['GAS', 'DIESEL', 'ELECTRIC'])),
  is_disabled: z.boolean(),
  application_fee_percentage: z.number(),
  is_promo: z.boolean(),
  labor_rate: z.number(),
  email: z.string().nullable(),
  metro_area: metroAreaSchema.nullable(),
})

export const providerBranchResponseSchema = z.object({ items: z.array(providerBranchSchema), count: z.number() })

export type ProviderBranch = z.infer<typeof providerBranchSchema>
export type ProviderBranchResponse = z.infer<typeof providerBranchResponseSchema>

export function useBranchesQuery(options?: { authorizedBranches?: boolean }) {
  const innerOptions = options ?? {}
  innerOptions.authorizedBranches = innerOptions.authorizedBranches ?? false
  const auth = useAuth()
  return useQuery({
    queryKey: ['branches', 'list', innerOptions],
    queryFn: async () => {
      if (!auth.token) {
        throw new Error('No token found')
      }

      const url = new URL(`${API_URL}/v2/provider/branch`)
      if (innerOptions.authorizedBranches) {
        url.searchParams.set('authorized_branches', 'true')
      }

      const response = await axios.get(url.toString(), {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return providerBranchResponseSchema.parse(response.data)
    },
  })
}

export function useBranchQuery(id: string) {
  const auth = useAuth()
  return useQuery({
    queryKey: ['branches', 'details', id],
    queryFn: async () => {
      if (!auth.token) {
        throw new Error('No token found')
      }
      const response = await axios.get(`${API_URL}/v2/provider/branch/${id}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return providerBranchSchema.parse(response.data)
    },
    enabled: !!id,
  })
}

/** Vehicle classes allowed in a partner branch */
export function useBranchVehicleClassesQuery(id: string) {
  const auth = useAuth()
  return useQuery({
    queryKey: ['vehicle-classes', 'list', id],
    queryFn: async () => {
      if (!auth.token) {
        throw new Error('No token found')
      }

      const response = await axios.get(`${API_URL}/v2/provider/branch/${id}/vehicle-classes`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return branchVehicleClassesResponseSchema.parse(response.data)
    },
    enabled: !!id,
  })
}

export function useUpdateBranchMutation() {
  const auth = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ branchId, data }: { branchId: string; data: UpdateBranchData }) => {
      if (!auth.token) {
        throw new Error('No token found')
      }
      const response = await axios.patch(`${API_URL}/v2/provider/branch/${branchId}`, data, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return providerBranchSchema.parse(response.data)
    },
    onSuccess: (data) => {
      showNotificationWithSuccess('Branch updated successfully', 'Success')
      queryClient.invalidateQueries({ queryKey: ['branches', 'list'] })
      queryClient.invalidateQueries({ queryKey: ['branches', 'detail', data.id] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to update branch')
    },
  })
}

export function useBranchImagesQuery(branchId: string) {
  const auth = useAuth()
  return useQuery({
    queryKey: ['branch-images', 'list', branchId],
    queryFn: async () => {
      if (!auth.token) {
        throw new Error('No token found')
      }
      const response = await axios.get(`${API_URL}/v2/provider/branch/${branchId}/images`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return branchImagesResponseSchema.parse(response.data)
    },
    enabled: !!branchId,
  })
}

export function useUploadBranchImageMutation() {
  const auth = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ branchId, file, isCover }: { branchId: string; file: File; isCover: boolean }) => {
      if (!auth.token) {
        throw new Error('No token found')
      }

      const formData = new FormData()
      formData.append('images', file)

      const queryParams = new URLSearchParams()
      if (isCover) {
        queryParams.append('is_cover', 'true')
      }

      await axios.post(
        `${API_URL}/v2/provider/branch/${branchId}/images${queryParams.toString() ? `?${queryParams.toString()}` : ''}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      return branchId
    },
    onSuccess: (branchId) => {
      queryClient.invalidateQueries({ queryKey: ['branch-images', 'list', branchId] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to upload image')
    },
  })
}

export function useDeleteBranchImageMutation() {
  const auth = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ branchId, imageId }: { branchId: string; imageId: string }) => {
      if (!auth.token) {
        throw new Error('No token found')
      }
      await axios.delete(`${API_URL}/v2/provider/branch/${branchId}/images/${imageId}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`,
        },
      })
      return { imageId, branchId }
    },
    onSuccess: ({ branchId }) => {
      queryClient.invalidateQueries({ queryKey: ['branch-images', 'list', branchId] })
    },
    onError: (error) => {
      showNotificationWithError(error, 'Failed to delete image')
    },
  })
}

const metroAreaListSchema = z.object({
  items: z.array(metroAreaSchema),
  count: z.number(),
})

export async function listMetroAreas() {
  const response = await axios.get(`${API_URL}/metro-area?is_active=true&limit=30&page=1`)
  return metroAreaListSchema.parse(response.data)
}

export function useListMetroAreasQuery() {
  return useQuery({
    queryKey: ['metro-areas', 'list'],
    queryFn: listMetroAreas,
    meta: { onError: showNotificationWithError },
    select: (data) => data.items,
  })
}

const vehicleClassListSchema = z.object({
  items: z.array(vehicleClassSchema),
})

export async function listVehicleClasses() {
  const response = await axios.get(`${API_URL}/vehicle-class`)
  return vehicleClassListSchema.parse(response.data)
}

export function useListVehicleClassesQuery() {
  return useQuery({
    queryKey: ['vehicle-classes', 'list'],
    queryFn: listVehicleClasses,
    meta: { onError: showNotificationWithError },
    select: (data) => data.items,
  })
}

// Places autocomplete (items: string[])
const placesAutocompleteSchema = z.object({
  items: z.array(z.string()),
})

async function fetchPlacesAutocomplete(value: string, radius: number = 50000) {
  const response = await axios.get(`${API_URL}/provider/places/autocomplete`, {
    params: { value, radius },
  })
  return placesAutocompleteSchema.parse(response.data)
}

export function usePlacesAutocompleteQuery(value: string) {
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  return useQuery({
    queryKey: ['places-autocomplete', value],
    queryFn: async () => {
      return new Promise<{ items: string[] }>((resolve) => {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(async () => {
          const data = await fetchPlacesAutocomplete(value, 50000)
          resolve(data)
        }, 100)
      })
    },
    enabled: !!value,
    staleTime: 0,
  })
}
